<template>
  <div class="multimedia-selector-app h-full overflow-hidden">
    <CFullScreenLoader v-if="isLoading" fit-parent />
    <iframe
      v-else
      :id="`multimedia-selector-${appId}`"
      ref="iframeRef"
      class="w-full h-full border-none"
      :src="iframeSrc"
      @load="onIframeLoad"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  useMultimediaSelectorApps,
  type MultimediaSelectorItem,
} from '@content-selector/composables/useMultimediaSelectorApps'
import CFullScreenLoader from '@lib/components/CFullScreenLoader/CFullScreenLoader.vue'
import { doesLocalOverrideExist, getLocalOverrideUrl } from '@lib/util/dev.util'
import { waitForIframeInitialize } from '@lib/utils/iframeInitialize'

const props = defineProps<{
  appId: string
}>()

const emit = defineEmits<{
  add: [item: MultimediaSelectorItem]
}>()

const { multimediaSelectorApps } = useMultimediaSelectorApps()

const isLoading = ref(true)
const iframeRef = ref<HTMLIFrameElement | null>(null)

// Get the app data from loaded apps
const currentApp = computed(() => multimediaSelectorApps.value.find((app) => app.id === props.appId))

// Update loading state when we have app data
let unwatch: ReturnType<typeof watch> | null = null
unwatch = watch(
  currentApp,
  (app) => {
    if (app) {
      isLoading.value = false
      unwatch?.()
    }
  },
  { immediate: true },
)

// Construct iframe source URL
const iframeSrc = computed(() => {
  if (!currentApp.value?.app_metadata?.name) return ''

  // Check for local development override
  if (doesLocalOverrideExist(currentApp.value.app_metadata.name)) {
    return getLocalOverrideUrl(currentApp.value.app_metadata.name) as string
  }

  return currentApp.value.content_url || ''
})

// Similar to ControlBar popup app implementation
const onIframeLoad = async () => {
  try {
    // Use the helper function to wait for iframe initialization
    waitForIframeInitialize(
      iframeRef.value,
      {
        context: 'contentSelector',
        appId: props.appId,
      },
      'multimedia_selector',
      // Callback function for receiving results
      (result?: any) => {
        if (result?.type === 'add-item' && result.item) {
          emit('add', result.item as MultimediaSelectorItem)
        } else if (result?.type === 'add-items' && result.items) {
          const items = result.items as MultimediaSelectorItem[]
          items.forEach((item) => emit('add', item))
        }
      },
      { componentName: 'MultimediaSelectorApp' },
    )
  } catch (err) {
    console.error('Error initializing multimedia selector app:', err)
  }
}
</script>
